import React, { useState, useEffect, useCallback } from 'react';
import Calendario from '../components/Calendario';
import { reunionesService } from '../services/api';
import ConfirmPopup from '../components/ConfirmPopup';
import './Reuniones.css';

const formatFechaDisplay = (dateKey) => {
  if (!dateKey) return '';
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  return date.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * Página Reuniones: calendario de cursadas editable (agregar, cambiar día/hora, quitar).
 * Afecta a las asistencias: las fechas efectivas son las que se muestran y editan aquí.
 */
const Reuniones = () => {
  const [reunionesData, setReunionesData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('day'); // 'day' | 'add' | 'edit'
  const [modalDateKey, setModalDateKey] = useState(null);
  const [modalEventos, setModalEventos] = useState([]);
  const [editCursoId, setEditCursoId] = useState(null);
  const [addCursoId, setAddCursoId] = useState(null);
  const [formFecha, setFormFecha] = useState('');
  const [formHora, setFormHora] = useState('09:00');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadReuniones = useCallback(async () => {
    try {
      const data = await reunionesService.list();
      setReunionesData(Array.isArray(data) ? data : []);
    } catch {
      setReunionesData([]);
    }
  }, []);

  useEffect(() => {
    loadReuniones();
  }, [loadReuniones]);

  const handleDayClick = (dateKey, eventos) => {
    setModalDateKey(dateKey);
    setModalEventos(eventos || []);
    setModalMode('day');
    setModalOpen(true);
    setError('');
  };

  const handleAgregarReunion = (prefillDateKey = null) => {
    setModalMode('add');
    setFormFecha(prefillDateKey || modalDateKey || '');
    setFormHora('09:00');
    setAddCursoId(reunionesData && reunionesData[0] ? reunionesData[0].id : null);
    setEditCursoId(null);
    setModalOpen(true);
    setError('');
  };

  const handleQuitar = async (cursoId, fecha) => {
    setSaving(true);
    setError('');
    try {
      await reunionesService.quitar(cursoId, fecha);
      await loadReuniones();
      setModalEventos((prev) => prev.filter((e) => e.type !== 'cursada' || e.curso_id !== cursoId));
      window.dispatchEvent(new Event('calendarRefresh'));
    } catch (err) {
      setError(err.response?.data?.error || 'Error al quitar la reunión.');
    } finally {
      setSaving(false);
    }
  };

  const handleCambiar = (ev) => {
    setEditCursoId(ev.curso_id);
    setFormFecha(modalDateKey);
    setFormHora(ev.hora || '09:00');
    setModalMode('edit');
    setError('');
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    const cursoId = modalMode === 'add' ? addCursoId : editCursoId;
    if (!cursoId) {
      setError(modalMode === 'add' ? 'Seleccioná un curso.' : 'Falta curso.');
      return;
    }
    if (!formFecha || !formHora) {
      setError('Fecha y hora son obligatorios.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (modalMode === 'edit' && modalDateKey !== formFecha) {
        await reunionesService.quitar(editCursoId, modalDateKey);
      }
      await reunionesService.upsert(cursoId, formFecha, formHora);
      await loadReuniones();
      window.dispatchEvent(new Event('calendarRefresh'));
      setModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const cursadaEventos = modalEventos.filter((e) => e.type === 'cursada');

  if (reunionesData === null) {
    return (
      <div className="reuniones-page">
        <div className="reuniones-loading">
          <div className="loading-spinner" />
          <p>Cargando reuniones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reuniones-page">
      <ConfirmPopup
        open={Boolean(error)}
        title="Error"
        message={error}
        confirmLabel="Aceptar"
        cancelLabel={null}
        onConfirm={() => setError('')}
        onCancel={() => setError('')}
        variant="danger"
      />
      <header className="reuniones-header">
        <h1>Reuniones</h1>
        <p>Calendario de cursadas. Podés agregar, cambiar día/hora o quitar reuniones; esto afecta las asistencias.</p>
        <div className="reuniones-actions">
          <button type="button" className="reuniones-btn reuniones-btn-add" onClick={() => handleAgregarReunion('')}>
            + Agregar reunión
          </button>
        </div>
      </header>
      <div className="reuniones-calendario-wrap">
        <Calendario
          reunionesData={reunionesData}
          cursosSource="docente"
          soloCursadas
          titulo="Cursadas"
          colorearCursadasPorCurso
          vistaAnioCompleto
          onDayClick={handleDayClick}
        />
      </div>

      {modalOpen && (
        <div className="reuniones-modal-backdrop" onClick={() => !saving && setModalOpen(false)}>
          <div className="reuniones-modal" onClick={(e) => e.stopPropagation()}>
            <div className="reuniones-modal-header">
              <h2>
                {modalMode === 'day' && `Reuniones del ${formatFechaDisplay(modalDateKey)}`}
                {modalMode === 'add' && 'Agregar reunión'}
                {modalMode === 'edit' && 'Cambiar día u hora'}
              </h2>
              <button type="button" className="reuniones-modal-close" onClick={() => !saving && setModalOpen(false)} aria-label="Cerrar">
                ×
              </button>
            </div>

            {modalMode === 'day' && (
              <div className="reuniones-modal-body">
                {cursadaEventos.length === 0 ? (
                  <p className="reuniones-modal-sin">No hay reuniones este día.</p>
                ) : (
                  <ul className="reuniones-modal-list">
                    {cursadaEventos.map((ev, i) => (
                      <li key={i} className="reuniones-modal-item">
                        <span><strong>{ev.curso_nombre}</strong> {ev.hora && `— ${ev.hora}`}</span>
                        <div className="reuniones-modal-item-actions">
                          <button type="button" className="reuniones-btn reuniones-btn-sm" onClick={() => handleCambiar(ev)} disabled={saving}>
                            Cambiar
                          </button>
                          <button type="button" className="reuniones-btn reuniones-btn-sm reuniones-btn-danger" onClick={() => handleQuitar(ev.curso_id, modalDateKey)} disabled={saving}>
                            Quitar
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <button type="button" className="reuniones-btn reuniones-btn-add" onClick={() => handleAgregarReunion(modalDateKey)}>
                  + Agregar reunión este día
                </button>
              </div>
            )}

            {(modalMode === 'add' || modalMode === 'edit') && (
              <form className="reuniones-modal-form" onSubmit={handleSubmitForm}>
                <div className="reuniones-modal-body">
                  {modalMode === 'add' && (
                    <div className="reuniones-form-group">
                      <label>Curso</label>
                      <select
                        value={addCursoId ?? ''}
                        onChange={(e) => setAddCursoId(e.target.value ? Number(e.target.value) : null)}
                        required
                      >
                        <option value="">Seleccionar curso</option>
                        {(reunionesData || []).map((c) => (
                          <option key={c.id} value={c.id}>{c.nombre}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="reuniones-form-group">
                    <label>Fecha</label>
                    <input
                      type="date"
                      value={formFecha}
                      onChange={(e) => setFormFecha(e.target.value)}
                      required
                    />
                  </div>
                  <div className="reuniones-form-group">
                    <label>Hora</label>
                    <input
                      type="time"
                      value={formHora}
                      onChange={(e) => setFormHora(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="reuniones-modal-footer">
                  <button type="button" className="reuniones-btn" onClick={() => setModalOpen(false)} disabled={saving}>
                    Cancelar
                  </button>
                  <button type="submit" className="reuniones-btn reuniones-btn-primary" disabled={saving}>
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reuniones;
